import { useState, useCallback, useEffect } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';

/**
 * Hook to load and manage user avatar from Matrix
 * 
 * This hook handles fetching the user's avatar with authentication,
 * creating blob URLs, and cleaning up old blob URLs to prevent memory leaks.
 * 
 * @param {string} userId - Matrix user ID
 * @param {string} displayName - User's display name (for initials fallback)
 * @param {number} [size=96] - Desired avatar size in pixels
 * 
 * @returns {Object} Avatar state and controls
 * @returns {string|null} avatarUrl - Blob URL or data URL of the avatar
 * @returns {Function} loadAvatar - Function to manually reload the avatar
 * @returns {Function} cleanup - Function to manually revoke the blob URL
 * 
 * @example
 * ```javascript
 * const { avatarUrl, loadAvatar } = useUserAvatar(
 *   '@user:matrix.org',
 *   'User Name',
 *   96
 * );
 * 
 * // Avatar URL updates automatically
 * <img src={avatarUrl} alt="Avatar" />
 * 
 * // Manually reload if needed
 * <button onClick={loadAvatar}>Refresh Avatar</button>
 * ```
 */
export const useUserAvatar = (userId, displayName, size = 96) => {
  const client = useMatrixClient();
  const { getUserAvatar } = useAvatar();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0); // Trigger re-fetch on avatar changes

  // Load avatar when dependencies change
  useEffect(() => {
    let isMounted = true;

    const loadAvatarAsync = async () => {
      if (!client || !userId) {
        logger.debug('useUserAvatar: No client or userId, skipping load');
        return;
      }

      try {
        logger.debug(`useUserAvatar: Loading avatar for ${userId}`);

        // Get avatar from context
        const url = await getUserAvatar(userId, displayName, size);

        if (isMounted) {
          setAvatarUrl(url);
          logger.debug(`useUserAvatar: Avatar loaded for ${userId}`);
        }
      } catch (error) {
        logger.error(`useUserAvatar: Error loading avatar for ${userId}:`, error);
        if (isMounted) {
          setAvatarUrl(null);
        }
      }
    };

    loadAvatarAsync();

    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, userId, displayName, size, refreshCounter, getUserAvatar]); // refreshCounter triggers re-fetch

  // Listen for custom avatar change events
  // This is triggered when the user uploads/removes their avatar via useProfile
  useEffect(() => {
    const handleAvatarChanged = (event) => {
      const { userId: changedUserId } = event.detail || {};
      logger.debug(`useUserAvatar: Received avatar change event for ${changedUserId}`);

      // Only reload if this is for our user
      if (changedUserId === userId) {
        logger.info(`useUserAvatar: Avatar changed for ${userId}, triggering refresh...`);
        // Increment counter to trigger the main effect to re-run
        // This ensures proper acquire/release balance
        setRefreshCounter(prev => prev + 1);
      }
    };

    window.addEventListener('matrix:avatar:changed', handleAvatarChanged);

    return () => {
      window.removeEventListener('matrix:avatar:changed', handleAvatarChanged);
    };
  }, [userId]);

  // Manual reload function for external use (e.g., refresh button)
  const loadAvatar = useCallback(() => {
    logger.debug(`useUserAvatar: Manual reload triggered for ${userId}`);
    setRefreshCounter(prev => prev + 1);
  }, [userId]);

  return {
    avatarUrl,
    loadAvatar,
  };
};

export default useUserAvatar;
